import { PlatformLinkingConnectionStatus, PlatformLinkingProviderEntity } from '@okletsplay/core';
import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit';
import { EntityStoreName } from 'app/enums/store';
import { apiRequestCall } from 'app/services/api';
import { ApiResource } from 'app/services/api/enums';
import { cancelationSubscribe } from 'app/services/api/utils';
import { entityStoreStatus } from 'app/utils/store/entity-store-status';
import getPkce from 'oauth-pkce';

import { entityAdapter } from './adapter';
import { PlatformLinkingState } from './interfaces';
import { PlatformLinkingStoreStatus } from './enums';
import { PlatformLinkingProvider } from '@okletsplay/core/src/modules/platform-linking/enums';

const PKCE_CODE_LENGTH = 52;

export const getPlatformLinkingProviderList = createAsyncThunk(
  `${EntityStoreName.PlatformLinking}/getList`,
  (_, { signal }): Promise<PlatformLinkingProviderEntity[]> => {
    const cancelToken = cancelationSubscribe(signal);

    return apiRequestCall<PlatformLinkingProviderEntity[]>({
      params: {
        method: 'GET',
        resource: ApiResource.UserConnections,
        cancelToken,
      },
    });
  }
);

export const generatePkce = createAsyncThunk(
  `${EntityStoreName.PlatformLinking}/generatePkce`,
  (): Promise<
    Partial<Record<PlatformLinkingProvider, { verifier: string; challenge: string }>>
  > => {
    return new Promise((resolve) => {
      getPkce(PKCE_CODE_LENGTH, (error, { verifier, challenge }) => {
        if (error) {
          resolve({ [PlatformLinkingProvider.Lichess]: { verifier: '', challenge: '' } });
        }

        resolve({ [PlatformLinkingProvider.Lichess]: { verifier, challenge } });
      });
    });
  }
);

export const unlinkProvider = createAsyncThunk(
  `${EntityStoreName.PlatformLinking}/unlinkProvider`,
  (platformId: PlatformLinkingProvider, { signal }): Promise<PlatformLinkingProviderEntity> => {
    const cancelToken = cancelationSubscribe(signal);

    return apiRequestCall<PlatformLinkingProviderEntity>({
      params: {
        method: 'DELETE',
        resource: ApiResource.UserConnections,
        urlPrefix: platformId,
        cancelToken,
      },
    });
  }
);

export const clearAllReducer = (state: PlatformLinkingState): void => {
  entityAdapter.removeAll(state);
};

const prepareLichessLink = (
  providers: PlatformLinkingProviderEntity[],
  pkce: Partial<Record<PlatformLinkingProvider, { verifier: string; challenge: string }>>
) => {
  if (!providers) {
    return [];
  }

  if (!pkce?.[PlatformLinkingProvider.Lichess]) {
    return providers;
  }

  const lichessIndex = providers.findIndex(
    (provider) => provider.platformId === PlatformLinkingProvider.Lichess
  );
  if (lichessIndex > -1 && pkce) {
    const lichess = {
      ...providers[lichessIndex],
      authorizeRedirectURI: `${providers[lichessIndex].authorizeRedirectURI}&code_challenge=${
        pkce?.[PlatformLinkingProvider.Lichess]?.challenge ?? ''
      }`,
    };

    providers.splice(lichessIndex, 1, lichess);
    return providers;
  }

  return providers;
};

export const registerReducers = (builder: ActionReducerMapBuilder<PlatformLinkingState>): void => {
  builder
    .addCase(getPlatformLinkingProviderList.pending, (state, action) => {
      entityStoreStatus.addRequestId(
        state,
        PlatformLinkingStoreStatus.Loading,
        action.meta.requestId
      );
    })
    .addCase(getPlatformLinkingProviderList.fulfilled, (state, action) => {
      const pkce = state.pkce as Partial<
        Record<PlatformLinkingProvider, { verifier: string; challenge: string }>
      >;

      entityAdapter.upsertMany(state, prepareLichessLink(action.payload, pkce));
      entityStoreStatus.removeRequestId(
        state,
        PlatformLinkingStoreStatus.Loading,
        action.meta.requestId
      );
    })
    .addCase(getPlatformLinkingProviderList.rejected, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        PlatformLinkingStoreStatus.Loading,
        action.meta.requestId
      );
    })
    .addCase(generatePkce.pending, (state, action) => {
      entityStoreStatus.addRequestId(
        state,
        PlatformLinkingStoreStatus.LoadingPkce,
        action.meta.requestId
      );
    })
    .addCase(generatePkce.fulfilled, (state, action) => {
      state.pkce = action.payload;
      const providers = Object.values(state.entities) as PlatformLinkingProviderEntity[];

      entityAdapter.upsertMany(state, prepareLichessLink(providers, action.payload));
      entityStoreStatus.removeRequestId(
        state,
        PlatformLinkingStoreStatus.LoadingPkce,
        action.meta.requestId
      );
    })
    .addCase(generatePkce.rejected, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        PlatformLinkingStoreStatus.LoadingPkce,
        action.meta.requestId
      );
    })
    .addCase(unlinkProvider.pending, (state, action) => {
      entityStoreStatus.addRequestId(
        state,
        PlatformLinkingStoreStatus.Unlinking,
        action.meta.requestId
      );
    })
    .addCase(unlinkProvider.fulfilled, (state, action) => {
      entityAdapter.updateOne(state, {
        id: action.meta.arg,
        changes: { connectionStatus: PlatformLinkingConnectionStatus.NotConnected },
      });
      entityStoreStatus.removeRequestId(
        state,
        PlatformLinkingStoreStatus.Unlinking,
        action.meta.requestId
      );
    })
    .addCase(unlinkProvider.rejected, (state, action) => {
      entityStoreStatus.removeRequestId(
        state,
        PlatformLinkingStoreStatus.Unlinking,
        action.meta.requestId
      );
    });
};
